import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bell, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.userAgentData?.mobile === 'boolean') {
    return navigator.userAgentData.mobile;
  }
  return /Android|iPhone|iPad|iPod|IEMobile|Opera Mini|HarmonyOS|HUAWEI|Mobile/i.test(navigator.userAgent);
};

const urlBase64ToUint8Array = (value) => {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((character) => character.charCodeAt(0)));
};

const getDeviceMetadata = async () => {
  let clientHints = {};
  if (navigator.userAgentData?.getHighEntropyValues) {
    try {
      clientHints = await navigator.userAgentData.getHighEntropyValues([
        'architecture',
        'model',
        'platform',
        'platformVersion'
      ]);
    } catch (error) {
      console.info('Device details are not available:', error);
    }
  }

  let deviceId = window.localStorage.getItem('nexohub-push-device-id');
  if (!deviceId) {
    deviceId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.localStorage.setItem('nexohub-push-device-id', deviceId);
  }

  return {
    deviceId,
    model: clientHints.model || '',
    platform: clientHints.platform || navigator.platform || '',
    platformVersion: clientHints.platformVersion || '',
    architecture: clientHints.architecture || '',
    mobile: navigator.userAgentData?.mobile ?? isMobileDevice(),
    brands: navigator.userAgentData?.brands || [],
    userAgent: navigator.userAgent || '',
    language: navigator.language || '',
    standalone: window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
  };
};

const saveSubscription = async (userId, subscription) => {
  const device = await getDeviceMetadata();
  await axios.post(`${API_URL}/api/push/subscribe`, {
    userId,
    subscription: subscription.toJSON(),
    permission: Notification.permission,
    device
  });
};

const getPushServiceWorker = async () => {
  const serviceWorkerUrl = `${process.env.PUBLIC_URL || ''}/service-worker.js`;
  let registration = await navigator.serviceWorker.getRegistration();
  if (registration && !registration.active && !registration.installing && !registration.waiting) {
    await registration.unregister();
    registration = null;
  }
  registration = registration || await navigator.serviceWorker.register(serviceWorkerUrl);
  if (registration.active) return registration;

  const worker = registration.installing || registration.waiting;
  if (!worker) throw new Error('El service worker no está activo.');

  await Promise.race([
    new Promise((resolve, reject) => {
      worker.addEventListener('statechange', () => {
        if (worker.state === 'activated') resolve();
        if (worker.state === 'redundant') reject(new Error('No se pudo activar el service worker.'));
      });
    }),
    new Promise((_, reject) => {
      window.setTimeout(() => reject(new Error('El service worker tardó demasiado en activarse.')), 10000);
    })
  ]);

  return registration;
};

const PwaPushPrompt = () => {
  const { currentUser } = useAuth();
  const [publicKey, setPublicKey] = useState('');
  const [visible, setVisible] = useState(false);
  const [permissionBlocked, setPermissionBlocked] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const preparePush = async () => {
      setVisible(false);
      setPermissionBlocked(false);
      setError('');
      if (!currentUser?.id || !isMobileDevice() || !('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return;
      if (Notification.permission === 'denied') {
        setPermissionBlocked(true);
        setVisible(true);
        return;
      }

      try {
        const { data } = await axios.get(`${API_URL}/api/push/public-key`);
        if (!active || !data?.publicKey) return;

        setPublicKey(data.publicKey);
        const registration = await getPushServiceWorker();
        const existing = await registration.pushManager.getSubscription();
        if (existing) {
          await saveSubscription(currentUser.id, existing);
          if (active) setVisible(false);
          return;
        }

        if (Notification.permission === 'granted') {
          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(data.publicKey)
          });
          await saveSubscription(currentUser.id, subscription);
          if (active) setVisible(false);
          return;
        }
        if (active) setVisible(true);
      } catch (requestError) {
        console.info('Push notifications are not available:', requestError);
        if (active) {
          setError('No fue posible preparar las notificaciones. Intenta nuevamente después de actualizar la PWA.');
          setVisible(true);
        }
      }
    };

    preparePush();
    return () => {
      active = false;
    };
  }, [currentUser?.id]);

  const enableNotifications = async () => {
    setEnabling(true);
    setError('');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setPermissionBlocked(permission === 'denied');
        setError(
          permission === 'denied'
            ? ''
            : 'Para recibir avisos debes seleccionar Permitir cuando Android lo solicite.'
        );
        setVisible(true);
        return;
      }

      const registration = await getPushServiceWorker();
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        await existingSubscription.unsubscribe();
      }
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
      await saveSubscription(currentUser.id, subscription);
      setVisible(false);
    } catch (requestError) {
      console.error('Could not enable push notifications:', requestError);
      const errorName = requestError?.name || 'Error';
      const errorMessage = requestError?.message || 'No se recibió información adicional.';
      setError(`No fue posible activar las notificaciones (${errorName}: ${errorMessage}).`);
    } finally {
      setEnabling(false);
    }
  };

  const dismiss = () => {
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className={`fixed bottom-4 left-4 right-4 z-[80] mx-auto max-w-md rounded-2xl border bg-white p-4 shadow-2xl ${permissionBlocked ? 'border-amber-300' : 'border-blue-200'}`}>
      <button
        type="button"
        onClick={dismiss}
        className="absolute right-3 top-3 rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        aria-label="Cerrar"
      >
        <X size={18} />
      </button>
      <div className="flex gap-3 pr-7">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${permissionBlocked ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-primary'}`}>
          <Bell size={20} />
        </div>
        <div>
          <p className="font-semibold text-slate-800">
            {permissionBlocked ? 'Notificaciones bloqueadas' : 'Activa las notificaciones'}
          </p>
          <p className="mt-1 text-sm text-slate-600">
            {permissionBlocked
              ? 'Para recibir avisos, permite las notificaciones de NexoHub desde la configuración de aplicaciones de tu dispositivo.'
              : 'Pulsa Activar y luego selecciona Permitir cuando Android lo solicite.'}
          </p>
        </div>
      </div>
      {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        {!permissionBlocked && (
          <button type="button" onClick={dismiss} className="rounded-xl px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">
            Ahora no
          </button>
        )}
        {permissionBlocked ? (
          <button
            type="button"
            onClick={dismiss}
            className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600"
          >
            Entendido
          </button>
        ) : (
        <button
          type="button"
          onClick={enableNotifications}
          disabled={enabling || !publicKey}
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60"
        >
          {enabling ? 'Activando...' : !publicKey ? 'Preparando...' : 'Activar'}
        </button>
        )}
      </div>
    </div>
  );
};

export default PwaPushPrompt;
